import { Brain, Waves, Sprout } from "lucide-react";
import { WisdomRush } from "@/components/games/WisdomRush";
import { CalmCurrent } from "@/components/games/CalmCurrent";
import { KarmaGarden } from "@/components/games/KarmaGarden";

// Central registry powering the indexable /games/:slug pages, Home tiles and Mindful Arcade.
export const GAMES = [
  {
    slug: "wisdom-rush",
    id: "wisdom",
    name: "Wisdom Rush",
    tagline: "Practice discernment",
    color: "#A78BFA",
    icon: Brain,
    Component: WisdomRush,
    genre: "Trivia & brain training",
    short: "A fast, free trivia-style brain game where you read a short scenario and pick the most skillful response.",
    seoTitle: "Wisdom Rush — Free Online Brain Trivia Game | InnerArcade",
    seoDescription: "Play Wisdom Rush free in your browser. Answer quick scenario-based questions, build a daily streak, and train your judgment. No download — play in seconds.",
    keywords: "free browser trivia game, brain training game, daily quiz game, mindfulness trivia, quick puzzle game online",
    intro: "Wisdom Rush turns everyday dilemmas into a quick, replayable trivia round. Each question presents a real-life scenario and a few possible responses — your job is to choose the most skillful one, then read why. It's a calm brain-training break that takes about two minutes.",
    howToPlay: [
      "Pick a topic or choose \u201cSurprise me\u201d for a mixed round.",
      "Read the short scenario and the question that follows.",
      "Tap the response you think is the most skillful.",
      "Read the explanation to learn the reasoning behind it.",
      "Finish the round to log the session and earn Journey XP.",
    ],
    scoring: "You get a round score out of the total questions answered, but there's no ranking of your worth \u2014 the goal is thoughtful choices, not a perfect score. Every completed round earns Journey XP toward your next level.",
    repeat: "Fresh daily questions, topic modes, and a practice streak give you a reason to return each day.",
    related: ["calm-current", "karma-garden"],
  },
  {
    slug: "calm-current",
    id: "calm",
    name: "Calm Current",
    tagline: "Return to the breath",
    color: "#2DD4BF",
    icon: Waves,
    Component: CalmCurrent,
    genre: "Relaxation & breathing",
    short: "A free guided breathing game \u2014 sync your breath with a flowing current for a one to ten minute reset.",
    seoTitle: "Calm Current — Free Breathing & Relaxation Game Online | InnerArcade",
    seoDescription: "Calm Current is a free browser breathing game. Follow the flowing current through 1, 3, 5 or 10-minute sessions to relax, refocus, and reset your mind.",
    keywords: "free breathing game, relaxation game online, focus game, mindfulness game for adults, calm down game, guided breathing exercise",
    intro: "Calm Current is a gentle breathing practice disguised as a game. A glowing orb expands and contracts on a flowing current \u2014 follow it to guide a coherent five-second inhale and five-second exhale. Choose a session length and let a scattered mind settle.",
    howToPlay: [
      "Choose a session length: 1, 3, 5 or 10 minutes.",
      "Press Begin practice to start the current.",
      "Breathe in as the orb grows, breathe out as it shrinks.",
      "If your attention wanders, gently return to the breath \u2014 there's no failing here.",
      "Complete the session to receive a short reflection and Journey XP.",
    ],
    scoring: "There's no high score \u2014 completing a full session is the win. Each finished practice earns Journey XP and builds your practice streak.",
    repeat: "Timed sessions and calming daily challenges make it easy to build a short breathing habit.",
    related: ["wisdom-rush", "karma-garden"],
  },
  {
    slug: "karma-garden",
    id: "karma",
    name: "Karma Garden",
    tagline: "Cultivate qualities",
    color: "#7BC96F",
    icon: Sprout,
    Component: KarmaGarden,
    genre: "Casual & progression",
    short: "A free casual choice game \u2014 everyday decisions grow patience, generosity, honesty and compassion in your garden.",
    seoTitle: "Karma Garden — Free Casual Choice & Collection Game | InnerArcade",
    seoDescription: "Play Karma Garden free online. Make everyday choices, watch your garden of qualities grow, and unlock progress over time. A calm, casual collection game.",
    keywords: "free casual game, choice based game online, collection game, progression game, growing game, mindful decision game",
    intro: "Karma Garden is a casual, choose-your-response game where small everyday decisions shape a living garden. Each choice nurtures qualities like patience, generosity, honesty and compassion \u2014 and the scenarios never spoil the \u201cright\u201d answer, so you decide what kind of gardener you want to be.",
    howToPlay: [
      "Press Encounter a moment to draw a short everyday scenario.",
      "Read the situation and the possible responses.",
      "Choose the response that feels right to you.",
      "See the symbolic consequence and a short reflection.",
      "Watch your garden's qualities grow with every choice you make.",
    ],
    scoring: "Your garden tracks four qualities that grow with your choices \u2014 there's no win or lose, just steady, symbolic progress. Every moment you tend earns Journey XP.",
    repeat: "Unlockable growth and weekly goals reward returning to tend your garden.",
    related: ["wisdom-rush", "calm-current"],
  },
];

export const gameBySlug = (slug) => GAMES.find((g) => g.slug === slug);
export const gameById = (id) => GAMES.find((g) => g.id === id);
